/**
 * TeamWorkingNow — live "who's on this job right now" strip for TEAM jobs.
 *
 * Rendered on the In-Progress and Paused job cards (JobsEnhanced). Computes
 * each crew member's state purely from the job document (no extra fetch):
 *   - Working    → has an OPEN work session (green, pulsing)
 *   - Traveling  → has an open travel segment (blue)
 *   - Off clock  → worked earlier session(s), none open (slate, shows hours)
 *   - Not started→ in the crew snapshot but no sessions yet (dim outline)
 *
 * Only renders for team jobs (assigned_team_id / is_team_job / multi-member
 * snapshot). Individual jobs render nothing.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Users, MapPin, LogOut, LogIn, Loader2 } from 'lucide-react';
import { formatTimeWithTimezone } from '../../utils/timezone';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionWorkerId = (s) => s.worker_id || s.user_id;
const sessionOpen = (s) => !(s.ended_at || s.end_time);

const memberStatus = (memberId, job) => {
  const sessions = (job.work_sessions || []).filter((s) => sessionWorkerId(s) === memberId);
  const open = sessions.find(sessionOpen);
  if (open) return { key: 'working', since: open.started_at || open.start_time };
  const travel = (job.travel_segments || []).find((t) => t.worker_id === memberId && !t.ended_at);
  if (travel) return { key: 'traveling', kind: travel.kind };
  if (sessions.length > 0) {
    const hours = sessions.reduce((a, s) => a + (Number(s.hours_worked) || 0), 0);
    return { key: 'off', hours };
  }
  return { key: 'not_started' };
};

const STYLES = {
  working: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
  traveling: 'bg-blue-500/10 border-blue-500/40 text-blue-300',
  off: 'bg-slate-500/10 border-slate-500/30 text-slate-300',
  not_started: 'bg-transparent border-[#2D3748] text-slate-500',
};

const DOTS = {
  working: 'bg-emerald-400 animate-pulse',
  traveling: 'bg-blue-400 animate-pulse',
  off: 'bg-slate-500',
  not_started: 'bg-slate-700',
};

export default function TeamWorkingNow({ job, getUserName, currentUserId, timezone, onChanged }) {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [busyId, setBusyId] = useState(null);
  const canActOnOthers = user?.role === 'admin' || user?.role === 'supervisor' || user?.is_super_user;
  const inCrew = (id) => (job.assigned_team_member_ids || []).includes(id) || job.assigned_to === id || (user?.team_id && user.team_id === job.assigned_team_id);

  // Step away = pause MY participation only (grey for me, teammates stay green).
  // Rejoin = come back onto the team job (green again; resumes a paused job).
  const act = async (memberId, action) => {
    setBusyId(memberId);
    try {
      const onBehalf = memberId !== currentUserId ? `?on_behalf_of=${memberId}` : '';
      const r = await axios.put(`${API}/jobs/${job.id}/${action}${onBehalf}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const d = r.data || {};
      if (action === 'step-away') {
        toast({ title: d.auto_paused ? 'Stepped away — job paused' : 'Stepped away', description: d.auto_paused ? 'Nobody is left on this job, so it was paused.' : `${d.still_on_job} teammate${d.still_on_job === 1 ? '' : 's'} still on the job.` });
      } else {
        const left = (d.stepped_away_from || []).map((x) => x.job_number || x.title).filter(Boolean);
        toast({ title: d.job_status === 'in_progress' && job.status === 'paused' ? 'Rejoined — job resumed' : 'Rejoined the team job', description: left.length ? `Stepped away from ${left.join(', ')}.` : undefined });
      }
      onChanged?.();
    } catch (e) {
      toast({ title: action === 'step-away' ? 'Could not step away' : 'Could not rejoin', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const snapshot = job.assigned_team_member_ids || [];
  const isTeamJob = !!(job.assigned_team_id || job.is_team_job || snapshot.length > 1);
  if (!isTeamJob) return null;

  // Crew = snapshot ∪ anyone who actually logged a session/travel on the job.
  const ids = new Set(snapshot);
  (job.work_sessions || []).forEach((s) => sessionWorkerId(s) && ids.add(sessionWorkerId(s)));
  (job.travel_segments || []).forEach((t) => t.worker_id && ids.add(t.worker_id));
  if (job.assigned_to) ids.add(job.assigned_to);
  if (ids.size === 0) return null;

  const nameOf = (memberId) => {
    if (job.crew_names && job.crew_names[memberId]) return job.crew_names[memberId];
    const s = (job.work_sessions || []).find((x) => sessionWorkerId(x) === memberId && x.worker_name);
    if (s) return s.worker_name;
    const t = (job.travel_segments || []).find((x) => x.worker_id === memberId && x.worker_name);
    if (t) return t.worker_name;
    const n = getUserName ? getUserName(memberId) : null;
    return n && n !== 'Unknown' ? n : 'Teammate';
  };

  const members = [...ids].map((id) => ({ id, name: nameOf(id), status: memberStatus(id, job) }));
  // Working first, then traveling, then off, then not started.
  const order = { working: 0, traveling: 1, off: 2, not_started: 3 };
  members.sort((a, b) => order[a.status.key] - order[b.status.key] || a.name.localeCompare(b.name));

  const workingCount = members.filter((m) => m.status.key === 'working').length;

  return (
    <div
      className="bg-[#0F1923] border border-[#2D3748] rounded-lg p-3"
      data-testid={`team-working-now-${job.id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#1E88E5]" />
        <span className="text-sm font-semibold text-white">Working now</span>
        {job.team_name && (
          <span className="text-xs text-slate-400">
            · {job.team_parent_name ? `${job.team_parent_name} › ${job.team_name}` : job.team_name}
          </span>
        )}
        {job.is_subteam_job && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30 font-medium"
            data-testid={`squad-badge-${job.id}`}
          >
            squad
          </span>
        )}
        <span
          className={`ml-auto text-xs font-medium ${workingCount > 0 ? 'text-emerald-400' : 'text-slate-500'}`}
          data-testid={`team-working-count-${job.id}`}
        >
          {workingCount > 0 ? `${workingCount} on the clock` : 'No one on the clock'}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {members.map((m) => {
          const st = m.status;
          let label = 'Not started';
          if (st.key === 'working') {
            label = `Working${st.since ? ` · since ${formatTimeWithTimezone(st.since, timezone || 'America/Toronto')}` : ''}`;
          } else if (st.key === 'traveling') {
            label = st.kind === 'travel_return' ? 'Returning' : st.kind === 'parts_pickup' ? 'Parts pickup' : 'Traveling';
          } else if (st.key === 'off') {
            label = `Off the clock${st.hours > 0 ? ` · ${st.hours.toFixed(1)}h logged` : ''}`;
          }
          return (
            <div
              key={m.id}
              className={`flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs ${STYLES[st.key]}`}
              data-testid={`crew-status-${job.id}-${m.id}`}
              title={`${m.name} — ${label}`}
            >
              <span className={`w-2 h-2 rounded-full ${DOTS[st.key]}`} />
              <span className="font-medium">
                {m.name}
                {m.id === currentUserId && <span className="opacity-70"> (you)</span>}
              </span>
              {st.key === 'traveling' && <MapPin className="w-3 h-3" />}
              <span className="opacity-80">{label}</span>
              {(m.id === currentUserId ? inCrew(m.id) : canActOnOthers) && st.key !== 'traveling' && ['in_progress', 'paused', 'accepted'].includes(job.status) && (
                st.key === 'working' ? (
                  <button type="button" onClick={(e) => { e.stopPropagation(); act(m.id, 'step-away'); }} disabled={busyId === m.id}
                    className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-current/40 hover:bg-white/10 text-[11px]"
                    title={m.id === currentUserId ? 'Pause my participation on this job (teammates stay on it)' : `Step ${m.name} away from this job`}
                    data-testid={`crew-step-away-${job.id}-${m.id}`}>
                    {busyId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />} Step away
                  </button>
                ) : (
                  <button type="button" onClick={(e) => { e.stopPropagation(); act(m.id, 'rejoin'); }} disabled={busyId === m.id}
                    className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[11px]"
                    title={m.id === currentUserId ? 'Join / come back onto this job' : `Put ${m.name} back on this job`}
                    data-testid={`crew-rejoin-${job.id}-${m.id}`}>
                    {busyId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogIn className="w-3 h-3" />} {st.key === 'not_started' ? 'Join' : 'Rejoin'}
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
